import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  UploadFileOutlined,
  ImageOutlined,
  PictureAsPdfOutlined,
  InsertDriveFileOutlined,
  VisibilityOutlined,
  DeleteOutline,
} from '@mui/icons-material';
import { garantiaLabel } from './garantiasUI';

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPT = 'image/*,application/pdf';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (mime) => (mime || '').startsWith('image/');

const dataURLtoBlob = (dataurl) => {
  const [head, body] = dataurl.split(',');
  const mime = (head.match(/:(.*?);/) || [])[1] || 'application/octet-stream';
  const bin = atob(body);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return new Blob([u8], { type: mime });
};

/**
 * Diálogo de adjuntos (documentos / fotos) de una garantía.
 * Compartido por la pantalla de Garantías y el tab dentro del detalle del préstamo.
 * Los archivos se guardan como base64 en la BD (patrón base64-en-BD de la app).
 */
export default function GarantiaAdjuntos({ open, onClose, garantia, canEdit, canDelete, onChanged }) {
  const [adjuntos, setAdjuntos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const garantiaId = garantia?.id;

  const fetchAdjuntos = useCallback(async () => {
    if (!garantiaId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${garantiaId}/adjuntos`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      setAdjuntos(Array.isArray(data) ? data : []);
    } catch {
      setError('No se pudieron cargar los adjuntos');
    } finally {
      setLoading(false);
    }
  }, [garantiaId]);

  useEffect(() => {
    if (open) fetchAdjuntos();
  }, [open, fetchAdjuntos]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-subir el mismo archivo
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError('El archivo supera el máximo de 5 MB');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${garantiaId}/adjuntos`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ nombre: file.name, tipo_mime: file.type, tamano: file.size, contenido: dataUrl }),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'No se pudo subir el archivo');
      }
      await fetchAdjuntos();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (adjunto) => {
    setBusyId(adjunto.id);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/adjuntos/${adjunto.id}/contenido`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error('No se pudo abrir el adjunto');
      const data = await res.json();
      const blob = dataURLtoBlob(data.contenido);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (adjunto) => {
    setBusyId(adjunto.id);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/adjuntos/${adjunto.id}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'No se pudo eliminar el adjunto');
      }
      await fetchAdjuntos();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const iconFor = (mime) => {
    if (isImage(mime)) return <ImageOutlined color="primary" />;
    if ((mime || '').includes('pdf')) return <PictureAsPdfOutlined color="error" />;
    return <InsertDriveFileOutlined color="action" />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Documentos y fotos
        <Typography variant="body2" color="text.secondary">{garantiaLabel(garantia)}</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {canEdit && (
          <Box
            component="label"
            htmlFor="garantia-adjunto-input"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              py: 2,
              mb: 2,
              cursor: uploading ? 'default' : 'pointer',
              color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            {uploading ? <CircularProgress size={18} /> : <UploadFileOutlined fontSize="small" />}
            <Typography variant="body2">
              {uploading ? 'Subiendo…' : 'Subir documento o foto (máx. 5 MB)'}
            </Typography>
            <input
              id="garantia-adjunto-input"
              type="file"
              accept={ACCEPT}
              hidden
              disabled={uploading}
              onChange={handleUpload}
            />
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : adjuntos.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No hay documentos adjuntos.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {adjuntos.map((a) => (
              <Box
                key={a.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                {iconFor(a.tipo_mime)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap title={a.nombre}>{a.nombre}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[formatSize(a.tamano), a.fecha].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Tooltip title="Ver / descargar">
                  <span>
                    <IconButton size="small" onClick={() => handlePreview(a)} disabled={busyId === a.id}>
                      {busyId === a.id ? <CircularProgress size={16} /> : <VisibilityOutlined fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                {canDelete && (
                  <Tooltip title="Eliminar">
                    <span>
                      <IconButton size="small" onClick={() => handleDelete(a)} disabled={busyId === a.id}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
